import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  Plus, Filter, ChevronLeft, ChevronRight, Copy, FileDown,
  Search, ChevronDown, X, Trash2, CalendarClock, Layers,
  ZoomIn, Maximize, Minimize,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { getMaxISOWeeks, ZOOM_LEVELS } from "./constants";
import SaveStatusIndicator from "./SaveStatusIndicator";

export default function PlanningHeader({
  isGlobalMode, activeProject, activeYear, activeCalendarWeek, dateRangeText,
  saveStatus, showSollIst, setShowSollIst,
  goToPreviousWeek, goToNextWeek, goToToday, handleYearChange, handleWeekChange,
  filterUserIds, setFilterUserIds, filterProjectId, setFilterProjectId,
  users, projects, employeeFilterOpen, setEmployeeFilterOpen,
  tempFilterUserIds, setTempFilterUserIds,
  employeeSearch, setEmployeeSearch, employeeFilterRef,
  handleAddClick, today,
  onPdfExport,
  selectedIds, handleBulkDelete, clearSelection,
  onSaveTemplate, onApplyTemplate, onManageRecurring,
  templates, recurringCount,
  zoom, setZoom,
}) {
  const zoomIcons = { compact: Minimize, standard: ZoomIn, large: Maximize };

  return (
    <>
      {/* Top header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div>
          <h2 className="text-[15px] font-semibold" style={{ color: "var(--tga-text-bright)" }}>Einsatzplanung</h2>
          <p className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>
            {isGlobalMode ? "Alle Projekte" : (activeProject?.name || "Projekt")} | H=Heute | ESC=Abbruch
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SaveStatusIndicator status={saveStatus} />

          {/* Zoom Toggle */}
          <div
            className="flex items-center rounded-md overflow-hidden"
            style={{ border: "1px solid var(--tga-border)", background: "var(--tga-bg-card)" }}
            data-testid="zoom-toggle"
          >
            {Object.entries(ZOOM_LEVELS).map(([key, cfg]) => {
              const Icon = zoomIcons[key];
              return (
                <button
                  key={key}
                  onClick={() => setZoom(key)}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium transition-colors duration-100"
                  style={{
                    background: zoom === key ? "var(--tga-blue-soft)" : "transparent",
                    color: zoom === key ? "var(--tga-blue)" : "var(--tga-text-muted)",
                    borderRight: key !== "large" ? "1px solid var(--tga-border)" : "none",
                  }}
                  title={cfg.label}
                  data-testid={`zoom-${key}`}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden sm:inline">{cfg.label}</span>
                </button>
              );
            })}
          </div>

          <Button variant="outline" onClick={onManageRecurring}
            className="h-7 text-[11px] px-2 relative border-[var(--tga-border)] bg-transparent hover:bg-[var(--tga-bg-hover)]"
            style={{ color: "var(--tga-text-secondary)" }}
            data-testid="manage-templates-btn">
            <CalendarClock className="w-3 h-3 mr-1" />Vorlagen
            {recurringCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-green-500 text-white text-[8px] w-4 h-4 flex items-center justify-center rounded-full font-bold">
                {recurringCount}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={onSaveTemplate}
            className="h-7 text-[11px] px-2 border-[var(--tga-border)] bg-transparent hover:bg-[var(--tga-bg-hover)]"
            style={{ color: "var(--tga-text-secondary)" }}
            data-testid="save-template-btn">
            <Layers className="w-3 h-3 mr-1" />KW speichern
          </Button>
          <Button variant="outline" onClick={onPdfExport}
            className="h-7 text-[11px] px-2 border-[var(--tga-border)] bg-transparent hover:bg-[var(--tga-bg-hover)]"
            style={{ color: "var(--tga-text-secondary)" }}
            data-testid="pdf-export-btn">
            <FileDown className="w-3 h-3 mr-1" />PDF
          </Button>
          <Button onClick={() => handleAddClick(today)}
            className="h-7 text-[11px] px-2"
            style={{ background: "var(--tga-blue)", color: "#fff" }}
            data-testid="add-assignment-btn">
            <Plus className="w-3 h-3 mr-1" />Einsatz
          </Button>
        </div>
      </div>

      {/* Navigation bar */}
      <div className="flex-shrink-0 mb-2 flex items-center justify-between">
        <div
          className="flex items-center gap-1 px-1.5 py-1 rounded-md"
          style={{ background: "var(--tga-bg-card)", border: "1px solid var(--tga-border)" }}
        >
          <button onClick={goToPreviousWeek}
            className="p-1 rounded transition-colors hover:bg-[var(--tga-bg-hover)]"
            title="Ctrl+←" data-testid="prev-week-btn">
            <ChevronLeft className="w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
          </button>
          <select value={activeCalendarWeek} onChange={(e) => handleWeekChange(parseInt(e.target.value))}
            className="appearance-none px-3 py-1 text-[13px] font-bold cursor-pointer focus:outline-none min-w-[70px] text-center rounded"
            style={{
              background: "var(--tga-bg-elevated)",
              color: "var(--tga-text-bright)",
              border: "none",
            }}
            data-testid="week-selector">
            {Array.from({ length: getMaxISOWeeks(activeYear) }, (_, i) => i + 1).map(w => (
              <option key={w} value={w}>KW {w}</option>
            ))}
          </select>
          <button onClick={goToNextWeek}
            className="p-1 rounded transition-colors hover:bg-[var(--tga-bg-hover)]"
            title="Ctrl+→" data-testid="next-week-btn">
            <ChevronRight className="w-4 h-4" style={{ color: "var(--tga-text-secondary)" }} />
          </button>
          <span className="text-[10px] mx-1" style={{ color: "var(--tga-text-dim)" }}>{dateRangeText}</span>
          <button onClick={goToToday}
            className="px-2 py-0.5 text-[11px] font-medium rounded transition-colors"
            style={{ color: "var(--tga-blue)", background: "var(--tga-blue-soft)" }}
            data-testid="go-today-btn">
            Heute
          </button>
          <select value={activeYear} onChange={(e) => handleYearChange(parseInt(e.target.value))}
            className="ml-1 text-[11px] px-1.5 py-0.5 cursor-pointer rounded focus:outline-none"
            style={{
              border: "1px solid var(--tga-border)",
              background: "var(--tga-bg-card)",
              color: "var(--tga-text-primary)",
            }}
            data-testid="year-selector">
            {[activeYear - 1, activeYear, activeYear + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-2 text-[10px]" style={{ color: "var(--tga-text-dim)" }}>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showSollIst} onChange={(e) => setShowSollIst(e.target.checked)} className="w-3 h-3" />
            Soll/Ist
          </label>
          <span><Copy className="w-2.5 h-2.5 inline" /> CMD+Drag=Kopie</span>
          <span>Mausziehen=Scroll</span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex-shrink-0 mb-2 flex items-center gap-2">
        <div className="relative" ref={employeeFilterRef}>
          <button onClick={() => { setEmployeeFilterOpen(!employeeFilterOpen); setTempFilterUserIds(filterUserIds.length > 0 ? filterUserIds : users.map(u => u.id)); setEmployeeSearch(""); }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md transition-colors"
            style={{
              border: `1px solid ${filterUserIds.length > 0 ? "var(--tga-blue)" : "var(--tga-border)"}`,
              background: filterUserIds.length > 0 ? "var(--tga-blue-soft)" : "var(--tga-bg-card)",
              color: filterUserIds.length > 0 ? "var(--tga-blue)" : "var(--tga-text-secondary)",
            }}
            data-testid="filter-user">
            {filterUserIds.length > 0 && <Filter className="w-2.5 h-2.5" />}
            <span>{filterUserIds.length === 0 ? "Alle MA" : filterUserIds.length === 1 ? users.find(u => u.id === filterUserIds[0])?.name || "1 MA" : `${filterUserIds.length} MA`}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {filterUserIds.length > 0 && (
            <button onClick={(e) => { e.stopPropagation(); setFilterUserIds([]); }}
              className="absolute -right-5 top-1/2 -translate-y-1/2 p-0.5 transition-colors"
              style={{ color: "var(--tga-text-dim)" }}>
              <X className="w-3 h-3" />
            </button>
          )}
          {employeeFilterOpen && (
            <div
              className="absolute top-full left-0 mt-1 w-56 z-50 rounded-lg shadow-lg"
              style={{
                background: "var(--tga-bg-card)",
                border: "1px solid var(--tga-border-strong)",
                boxShadow: "var(--tga-shadow-lg)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-1.5" style={{ borderBottom: "1px solid var(--tga-border)" }}>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: "var(--tga-text-dim)" }} />
                  <input type="text" placeholder="Suchen..." value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="w-full h-7 pl-6 pr-2 text-[11px] rounded focus:outline-none"
                    style={{
                      background: "var(--tga-bg-content)",
                      border: "1px solid var(--tga-border)",
                      color: "var(--tga-text-primary)",
                    }}
                    autoFocus />
                </div>
              </div>
              <div className="px-1.5 py-0.5" style={{ borderBottom: "1px solid var(--tga-border)" }}>
                <label className="flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer hover:bg-[var(--tga-bg-hover)]">
                  <input type="checkbox" checked={tempFilterUserIds.length === users.length}
                    onChange={(e) => setTempFilterUserIds(e.target.checked ? users.map(u => u.id) : [])} className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-medium" style={{ color: "var(--tga-text-primary)" }}>(Alle)</span>
                </label>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {users.filter(u => u.name.toLowerCase().includes(employeeSearch.toLowerCase())).map((u) => (
                  <label key={u.id} className="flex items-center gap-1.5 px-3 py-1 cursor-pointer hover:bg-[var(--tga-bg-hover)]">
                    <input type="checkbox" checked={tempFilterUserIds.includes(u.id)}
                      onChange={(e) => setTempFilterUserIds(e.target.checked ? [...tempFilterUserIds, u.id] : tempFilterUserIds.filter(id => id !== u.id))}
                      className="w-3.5 h-3.5" />
                    <span className="text-[11px]" style={{ color: "var(--tga-text-primary)" }}>{u.name}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center justify-between p-1.5" style={{ borderTop: "1px solid var(--tga-border)", background: "var(--tga-bg-elevated)" }}>
                <button onClick={() => setTempFilterUserIds(users.map(u => u.id))}
                  className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>Reset</button>
                <div className="flex gap-1">
                  <button onClick={() => setEmployeeFilterOpen(false)}
                    className="px-2 py-1 text-[11px] rounded" style={{ color: "var(--tga-text-muted)" }}>Abb</button>
                  <button onClick={() => { setFilterUserIds(tempFilterUserIds.length === users.length ? [] : tempFilterUserIds); setEmployeeFilterOpen(false); }}
                    className="px-2 py-1 text-[11px] rounded font-medium"
                    style={{ background: "var(--tga-blue)", color: "#fff" }}>OK</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {isGlobalMode && (
          <div className="flex items-center gap-1">
            <Filter className="w-3 h-3" style={{ color: "var(--tga-text-dim)" }} />
            <Select value={filterProjectId || "all"} onValueChange={(v) => setFilterProjectId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] h-7 text-[11px]" data-testid="filter-project"
                style={{ background: "var(--tga-bg-card)", border: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}>
                <SelectValue placeholder="Alle Projekte" />
              </SelectTrigger>
              <SelectContent
                style={{ background: "var(--tga-bg-card)", border: "1px solid var(--tga-border-strong)" }}>
                <SelectItem value="all">Alle Projekte</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || "#6B7280" }} />
                      {p.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed top-[60px] left-0 right-0 z-40 flex items-center justify-between px-6 py-2"
          style={{ backgroundColor: "hsl(218 30% 12%)", borderBottom: "1px solid rgba(59,130,246,0.3)" }}
          data-testid="bulk-action-bar">
          <span className="text-[12px] font-semibold" style={{ color: "var(--tga-blue)" }}>
            {selectedIds.size} ausgewaehlt
          </span>
          <div className="flex items-center gap-1.5">
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} className="h-7 text-[11px]" data-testid="bulk-delete-btn">
              <Trash2 className="w-3 h-3 mr-1" />Loeschen ({selectedIds.size})
            </Button>
            <Button variant="outline" size="sm" onClick={clearSelection}
              className="bg-transparent border-white/20 text-white/60 hover:bg-white/5 hover:text-white h-7 text-[11px]">
              <X className="w-3 h-3 mr-1" />Aufheben
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
